import React, { useEffect, useRef, useState } from 'react'
import { ArrowUpTrayIcon, EyeIcon, PaperClipIcon } from '@heroicons/react/24/outline'
import { Button } from '../../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../../config/api'

function docLabel(d) {
  if (typeof d === 'string') return d
  return d?.file_name || d?.name || d?.filename || 'Document'
}

function docId(d) {
  if (d == null || typeof d === 'string') return null
  if (d.id == null || d.id === '') return null
  const n = Number(d.id)
  return Number.isFinite(n) ? String(d.id) : null
}

function normalizeDoc(row, fallbackName = null) {
  if (!row || typeof row !== 'object') return null
  const fileName = row.file_name || row.name || row.filename || fallbackName || null
  const id = row.id != null && row.id !== '' ? String(row.id) : null
  return {
    id,
    file_name: fileName || 'Document',
    mime_type: row.mime_type || null,
    size_bytes: row.size_bytes != null ? row.size_bytes : null,
    storage_url: row.storage_url || null,
    blob_path: row.blob_path || null,
    section: row.section || null,
    at: row.uploaded_at || row.at || null,
  }
}

/**
 * Upload / list / view attachments for an ECDD form section (SoF, activity).
 */
export default function EcddSectionAttachments({
  ecddId,
  section,
  docs = [],
  onChange,
  readOnly = false,
  title = 'Attachments',
  hint = 'Upload supporting documents for this section.',
}) {
  const fileInputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [openingKey, setOpeningKey] = useState(null)
  const list = Array.isArray(docs) ? docs : []

  // Always reconcile with server so every chip has id + file_name (enables View).
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!ecddId || !section || !onChange) return
      try {
        const res = await authFetch(
          getLocalApiUrl(
            `/api/ecdd/${encodeURIComponent(ecddId)}/documents?section=${encodeURIComponent(section)}`
          )
        )
        const j = await res.json().catch(() => ({}))
        if (cancelled || !res.ok || !j.success) return
        const serverDocs = (Array.isArray(j.data) ? j.data : []).map((r) => normalizeDoc(r)).filter(Boolean)
        if (!serverDocs.length) return

        const byId = new Map(serverDocs.filter((d) => d.id).map((d) => [String(d.id), d]))
        const byName = new Map()
        for (const d of serverDocs) {
          const key = String(d.file_name || '').toLowerCase()
          if (key && key !== 'document' && key !== 'file') byName.set(key, d)
        }

        if (list.length === 0) {
          onChange(serverDocs)
          return
        }

        let changed = false
        const merged = list.map((d) => {
          if (typeof d === 'string') {
            const hit = byName.get(d.toLowerCase())
            if (hit) {
              changed = true
              return hit
            }
            return { file_name: d }
          }
          const id = docId(d)
          if (id && byId.has(id)) {
            const hit = byId.get(id)
            if (
              hit.file_name !== d.file_name ||
              !d.file_name ||
              d.file_name === 'file' ||
              d.file_name === 'Document'
            ) {
              changed = true
              return { ...d, ...hit }
            }
            return d
          }
          const nameKey = String(d.file_name || d.name || '').toLowerCase()
          const hit = nameKey ? byName.get(nameKey) : null
          if (hit) {
            changed = true
            return { ...d, ...hit }
          }
          // Placeholder "file" with no id — try newest unmatched server doc
          if ((!d.file_name || d.file_name === 'file' || d.file_name === 'Document') && !id) {
            const unused = serverDocs.find(
              (s) =>
                s.id &&
                !list.some(
                  (x) => typeof x !== 'string' && docId(x) && String(docId(x)) === String(s.id)
                )
            )
            if (unused) {
              changed = true
              return unused
            }
          }
          return d
        })

        const usedIds = new Set(merged.map((d) => docId(d)).filter(Boolean))
        for (const s of serverDocs) {
          if (s.id && !usedIds.has(String(s.id))) {
            merged.push(s)
            changed = true
          }
        }

        if (changed) onChange(merged)
      } catch {
        /* non-fatal */
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ecddId, section])

  const handleFilesSelected = async (e) => {
    const files = e.target.files
    if (!files?.length || readOnly) return
    if (!ecddId) {
      window.alert('Cannot upload: ECDD record not loaded yet')
      e.target.value = ''
      return
    }

    setUploading(true)
    const uploaded = []
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData()
        fd.append('file', file)
        const res = await authFetch(
          getLocalApiUrl(
            `/api/ecdd/${encodeURIComponent(ecddId)}/documents?section=${encodeURIComponent(section || 'general')}`
          ),
          { method: 'POST', body: fd }
        )
        const j = await res.json().catch(() => ({}))
        if (res.ok && j.success !== false) {
          const row = normalizeDoc(j.data || {}, file.name)
          // If serializer still stripped fields, re-fetch list for this file
          if (!row?.id || row.file_name === 'Document' || row.file_name === 'file') {
            try {
              const listRes = await authFetch(
                getLocalApiUrl(
                  `/api/ecdd/${encodeURIComponent(ecddId)}/documents?section=${encodeURIComponent(section || 'general')}`
                )
              )
              const listJ = await listRes.json().catch(() => ({}))
              const serverDocs = (Array.isArray(listJ.data) ? listJ.data : [])
                .map((r) => normalizeDoc(r))
                .filter(Boolean)
              const match =
                serverDocs.find(
                  (d) =>
                    String(d.file_name || '').toLowerCase() === String(file.name).toLowerCase()
                ) || serverDocs[0]
              uploaded.push(
                match || {
                  file_name: file.name,
                  mime_type: file.type || null,
                  size_bytes: file.size,
                  section,
                  at: new Date().toISOString(),
                }
              )
            } catch {
              uploaded.push({
                ...row,
                file_name: file.name,
                mime_type: file.type || null,
                size_bytes: file.size,
                section,
                at: new Date().toISOString(),
              })
            }
          } else {
            uploaded.push({
              ...row,
              file_name: row.file_name || file.name,
              mime_type: row.mime_type || file.type || null,
              size_bytes: row.size_bytes != null ? row.size_bytes : file.size,
              section: row.section || section,
              at: row.at || new Date().toISOString(),
            })
          }
        } else {
          console.error(`Upload failed for ${file.name}:`, j.error || res.status)
        }
      }
      if (uploaded.length > 0 && onChange) {
        onChange([...list, ...uploaded])
      }
      if (uploaded.length < files.length) {
        window.alert(`Uploaded ${uploaded.length} of ${files.length} file(s). See console for details.`)
      }
    } catch (err) {
      console.error('ECDD section upload failed:', err)
      window.alert('Failed to upload one or more files')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const removeDoc = (idOrName, index) => {
    if (readOnly || !onChange) return
    onChange(
      list.filter((d, i) => {
        if (i === index) return false
        if (typeof d === 'string') return d !== idOrName
        return String(d.id) !== String(idOrName) && d.file_name !== idOrName
      })
    )
  }

  const resolveIdForView = async (d) => {
    let id = docId(d)
    if (id) return id
    if (!ecddId) return null
    try {
      const res = await authFetch(
        getLocalApiUrl(
          `/api/ecdd/${encodeURIComponent(ecddId)}/documents?section=${encodeURIComponent(section || 'general')}`
        )
      )
      const j = await res.json().catch(() => ({}))
      const serverDocs = (Array.isArray(j.data) ? j.data : []).map((r) => normalizeDoc(r)).filter(Boolean)
      const name = String(docLabel(d) || '').toLowerCase()
      const hit =
        serverDocs.find((s) => String(s.file_name || '').toLowerCase() === name) ||
        (serverDocs.length === 1 ? serverDocs[0] : null)
      if (hit?.id && onChange) {
        onChange(
          list.map((x) => {
            if (x === d) return { ...(typeof x === 'object' ? x : { file_name: x }), ...hit }
            return x
          })
        )
        return String(hit.id)
      }
    } catch {
      /* ignore */
    }
    return null
  }

  const viewDoc = async (d, index) => {
    const key = `${docId(d) || docLabel(d)}-${index}`
    setOpeningKey(key)
    try {
      const id = await resolveIdForView(d)
      if (!ecddId || !id) {
        window.alert('This file cannot be opened yet — try refreshing the page, or re-upload it.')
        return
      }
      const res = await authFetch(
        getLocalApiUrl(
          `/api/ecdd/${encodeURIComponent(ecddId)}/documents/${encodeURIComponent(id)}/download`
        )
      )
      if (!res.ok) {
        const j = await res.json().catch(() => ({}))
        throw new Error(j.error || `Open failed (${res.status})`)
      }
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const w = window.open(objectUrl, '_blank', 'noopener,noreferrer')
      if (!w) {
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = docLabel(d)
        a.click()
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    } catch (err) {
      console.error('ECDD document open failed:', err)
      window.alert(err.message || 'Could not open document')
    } finally {
      setOpeningKey(null)
    }
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-3.5 py-3 mb-4">
      <div className="text-[13px] font-semibold text-slate-700 mb-1">{title}</div>
      {hint ? <p className="text-xs text-slate-500 m-0 mb-2">{hint}</p> : null}

      {list.length > 0 ? (
        <div className="flex gap-2 flex-wrap mb-2">
          {list.map((d, i) => {
            const key = typeof d === 'string' ? `${d}-${i}` : `${d.id || docLabel(d)}-${i}`
            const label = docLabel(d)
            const opening = openingKey === key
            return (
              <span
                key={key}
                className="inline-flex gap-1.5 items-center px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-sm text-slate-600"
              >
                <PaperClipIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="max-w-[220px] truncate" title={label}>
                  {label}
                </span>
                <button
                  type="button"
                  className="inline-flex items-center gap-0.5 text-xs font-semibold text-psg-teal hover:underline bg-transparent border-none cursor-pointer ml-0.5 disabled:opacity-50"
                  onClick={() => viewDoc(d, i)}
                  disabled={!!opening || !ecddId}
                  title="View document"
                >
                  <EyeIcon className="w-3.5 h-3.5" />
                  {opening ? 'Opening…' : 'View'}
                </button>
                {!readOnly && (
                  <button
                    type="button"
                    className="text-xs text-red-600 hover:underline bg-transparent border-none cursor-pointer ml-0.5"
                    onClick={() => removeDoc(typeof d === 'string' ? d : d.id || d.file_name, i)}
                  >
                    Remove
                  </button>
                )}
              </span>
            )
          })}
        </div>
      ) : (
        <p className="text-xs text-slate-400 m-0 mb-2">No documents attached yet.</p>
      )}

      {!readOnly && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            multiple
            onChange={handleFilesSelected}
            disabled={uploading}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-psg-teal text-psg-teal hover:bg-psg-teal/5"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || !ecddId}
          >
            <ArrowUpTrayIcon className="w-3.5 h-3.5 mr-1" />
            {uploading ? 'Uploading…' : 'Upload Document'}
          </Button>
        </>
      )}
    </div>
  )
}
